/* High-intent SEO guide pages. Each targets a top-searched UK query and funnels
   into the matching calculator + Pro signup. Content is informational and clearly
   flagged as estimates. */

export const GUIDES = [
  {
    slug: 'resin-driveway-cost',
    h1: 'How Much Does a Resin Driveway Cost in 2026?',
    metaTitle: 'Resin Driveway Cost Per m² (2026 UK Guide)',
    metaDescription:
      'How much does a resin driveway cost in the UK? 2026 prices per m², total costs for a 2-car drive, what affects the price, and a free resin calculator.',
    kicker: '// Cost Guide',
    updated: 'June 2026',
    intro: [
      'A resin driveway is one of the most popular modern surfacing choices in the UK — seamless, low-maintenance and permeable. But the big question every homeowner (and tradesperson pricing the job) asks is simple: how much does it actually cost?',
      'Below are realistic 2026 UK price ranges per square metre, what pushes the price up or down, and a free calculator to work out exactly how many resin kits and how much aggregate you need for any area.',
    ],
    costTable: {
      caption: 'Resin driveway cost per m² (supplied & installed, 2026 UK)',
      head: ['Scenario', 'Cost per m²', 'Typical 50 m² drive'],
      rows: [
        ['On top of solid concrete / tarmac', '£50 – £110', '£2,500 – £5,500'],
        ['Standard install (solid base)', '£100 – £120', '£5,000 – £6,000'],
        ['New sub-base required (excavation)', '£130 – £165', '£6,500 – £8,250'],
      ],
    },
    sections: [
      {
        h: 'What affects the price most?',
        p: ['The single biggest factor is the base. If you already have sound concrete or tarmac, the resin can go straight on top and the job is far cheaper. If the area needs digging out, a new MOT Type 1 sub-base and edgings, costs climb significantly.'],
        ul: [
          'Existing base condition (solid vs. needs excavation)',
          'Aggregate choice — premium UV-stable stone costs more',
          'Resin Bound (smooth, permeable) vs. Resin Bonded (scattered, textured)',
          'Region — London & the South East run 15–20% higher',
          'Access, edgings, drainage channels and prep work',
        ],
      },
      {
        h: 'How much resin and aggregate do you need?',
        p: [
          'A standard 7.5kg UVR resin kit covers roughly 3.55 m² at 18mm depth for a driveway, or about 4.0 m² at 15mm for foot traffic. Each kit is typically mixed with around 75kg of 2–5mm aggregate plus 25kg of 1–3mm and kiln-dried sand.',
          'Rather than guessing, drop your length and width into the Resin Driveway Calculator below — it works out kits, resin, aggregate, sand and primer instantly, with a wastage allowance.',
        ],
      },
      {
        h: 'Resin bound vs. block paving — which is cheaper?',
        p: ['If you already have a solid base, resin is often cheaper than block paving because it is laid much faster (less labour). If you need a new sub-base for both, block paving can come out similar or slightly cheaper. Resin wins on low maintenance and drainage; block paving offers more traditional pattern options.'],
      },
    ],
    faq: [
      { q: 'How much is a resin driveway for a typical 2-car drive?', a: 'Expect roughly £5,000–£8,000 for a ~50 m² drive in 2026, depending on whether a new sub-base is needed. On a sound existing base it can be considerably less.' },
      { q: 'Is resin cheaper than block paving?', a: 'Usually yes if you already have a solid concrete or tarmac base, because resin is faster to lay. If both need a new sub-base, the totals are often similar.' },
      { q: 'How long does a resin driveway last?', a: 'A well-installed resin bound driveway typically lasts 15–25 years with minimal maintenance — usually just an occasional power wash.' },
    ],
    calc: { slug: 'resin-driveway', label: 'Resin Driveway Calculator', blurb: 'Work out kits, resin, aggregate, sand and primer for any area.' },
    relatedGuides: ['block-paving-cost', 'patio-cost'],
  },

  {
    slug: 'block-paving-cost',
    h1: 'How Much Does Block Paving Cost Per m²?',
    metaTitle: 'Block Paving Cost Per m² (2026 UK Guide)',
    metaDescription:
      'Block paving cost per square metre in the UK for 2026 — materials vs. installed prices, total cost for a driveway, and a free block paving calculator.',
    kicker: '// Cost Guide',
    updated: 'June 2026',
    intro: [
      'Block paving remains the most popular traditional driveway choice in the UK — hard-wearing, repairable and available in countless colours and patterns. Here is what it costs in 2026 and how to work out exactly how many blocks you need.',
    ],
    costTable: {
      caption: 'Block paving cost per m² (2026 UK)',
      head: ['Scenario', 'Cost per m²', 'Typical 50 m² drive'],
      rows: [
        ['Materials only (blocks + sand)', '£40 – £80', '£2,000 – £4,000'],
        ['Supplied & installed (incl. sub-base)', '£80 – £135', '£4,000 – £6,750'],
        ['Premium blocks / intricate patterns', '£120 – £160', '£6,000 – £8,000'],
      ],
    },
    sections: [
      {
        h: 'Materials vs. labour',
        p: ['Block paving is labour-intensive because each block is laid by hand, so installation makes up a large share of the cost. The sub-base (excavation + MOT Type 1) and edge restraints are essential and often underestimated.'],
        ul: [
          'Block type — standard concrete is cheapest; clay and premium pavers cost more',
          'Pattern — herringbone and basketweave take longer to lay',
          'Sub-base depth and ground conditions',
          'Cutting around curves, manholes and borders',
        ],
      },
      {
        h: 'How many blocks do you need?',
        p: ['Standard 200×100mm blocks work out at roughly 50 blocks per m² (laid). Always add 5–10% for cuts and breakages. The Block Paving Calculator below gives you the block count and area instantly.'],
      },
    ],
    faq: [
      { q: 'How much does block paving cost to install?', a: 'Supplied and installed, expect about £80–£135 per m² in 2026, so roughly £4,000–£6,750 for a typical 50 m² driveway.' },
      { q: 'How many blocks are there in a square metre?', a: 'About 50 standard 200×100mm blocks per m². Add 5–10% extra for cuts and wastage.' },
    ],
    calc: { slug: 'block-paving', label: 'Block Paving Calculator', blurb: 'Work out blocks required and area for any driveway or patio.' },
    relatedGuides: ['resin-driveway-cost', 'patio-cost'],
  },

  {
    slug: 'patio-cost',
    h1: 'How Much Does a Patio Cost in the UK?',
    metaTitle: 'Patio Cost Per m² (2026 UK Guide)',
    metaDescription:
      'How much does a patio cost in 2026? UK prices per m² for budget and premium patios, total costs, and a free paving slabs calculator.',
    kicker: '// Cost Guide',
    updated: 'June 2026',
    intro: [
      'A new patio is one of the most rewarding outdoor jobs — but prices vary hugely depending on the paving you choose. Here are 2026 UK costs per square metre and how to work out the number of slabs you need.',
    ],
    costTable: {
      caption: 'Patio cost per m² (supplied & installed, 2026 UK)',
      head: ['Type', 'Cost per m²', 'Typical 20 m² patio'],
      rows: [
        ['Budget (plain concrete / basic slab)', '£70 – £90', '£1,400 – £1,800'],
        ['Mid-range (porcelain / sandstone)', '£90 – £120', '£1,800 – £2,400'],
        ['Premium (natural stone / high-end)', '£110 – £140+', '£2,200 – £2,800+'],
      ],
    },
    sections: [
      {
        h: 'What drives the cost?',
        p: ['Material choice is the biggest lever, followed by groundworks (excavation and sub-base), edgings and any steps or features. Porcelain has surged in popularity for its low maintenance and crisp finish.'],
        ul: ['Paving material and thickness', 'Sub-base and excavation', 'Pointing/jointing method', 'Falls for drainage and any steps'],
      },
      {
        h: 'How many slabs do you need?',
        p: ['The number of slabs depends on slab size and your patio area, plus a cutting allowance. Use the Paving Slabs Calculator below to get the exact count for any slab size and area.'],
      },
    ],
    faq: [
      { q: 'How much does a 20m² patio cost?', a: 'Typically £1,400–£2,800 installed in 2026, depending on whether you choose budget concrete slabs or premium natural stone/porcelain.' },
      { q: 'What fall should a patio have for drainage?', a: 'A fall of about 1:80 (roughly 12.5mm per metre) away from the house is the usual guide. See our patio fall guide and calculator.' },
    ],
    calc: { slug: 'paving-slabs', label: 'Paving Slabs Calculator', blurb: 'Work out how many slabs you need for any area and slab size.' },
    relatedGuides: ['block-paving-cost', 'how-many-paving-slabs-do-i-need'],
  },

  {
    slug: 'how-many-bricks-per-m2',
    h1: 'How Many Bricks Per Square Metre?',
    metaTitle: 'How Many Bricks Per m²? (UK Brick Calculator Guide)',
    metaDescription:
      'How many bricks per square metre for a UK wall? The standard figure, allowances for wastage, and a free brick calculator for any wall size.',
    kicker: '// How-To',
    updated: 'June 2026',
    intro: [
      'Whether you are pricing a job or buying materials, knowing how many bricks you need per square metre saves money and avoids a second trip to the merchant.',
    ],
    sections: [
      {
        h: 'The standard figure',
        p: ['For a standard UK metric brick (215 × 102.5 × 65mm) laid in a single skin with a 10mm mortar joint, you need about 60 bricks per square metre. For a double-skin (one-brick-thick) wall, double it to roughly 120 per m².'],
        ul: ['Single skin (half-brick): ~60 bricks per m²', 'Double skin (one brick): ~120 bricks per m²', 'Always add 5% for cuts and breakages'],
      },
      {
        h: 'Don\'t forget mortar and wastage',
        p: ['A typical wall uses around 0.02–0.03 m³ of mortar per m² of single-skin brickwork. Always order a little extra brick to cover cuts, returns and the odd damaged unit. The Brick Calculator below handles the count and wastage for you.'],
      },
    ],
    faq: [
      { q: 'How many bricks are in a square metre?', a: 'About 60 standard UK bricks per m² for a single-skin wall (with 10mm joints), or roughly 120 per m² for a one-brick-thick wall. Add ~5% for wastage.' },
      { q: 'How many bricks do I need for a wall?', a: 'Multiply the wall area (m²) by 60 (single skin) or 120 (double skin), then add 5%. The calculator below does this automatically.' },
    ],
    calc: { slug: 'bricks-per-area', label: 'Brick Calculator', blurb: 'Work out bricks needed for any wall, with wastage.' },
    relatedGuides: ['sand-and-cement-mortar-mix'],
  },

  {
    slug: 'how-much-concrete-do-i-need',
    h1: 'How Much Concrete Do I Need?',
    metaTitle: 'How Much Concrete Do I Need? (m³ Calculator Guide)',
    metaDescription:
      'Work out how much concrete you need in m³ for a slab, footing or base, plus how many bags or how to order ready-mix. Free concrete calculator.',
    kicker: '// How-To',
    updated: 'June 2026',
    intro: [
      'Concrete is sold by the cubic metre (m³), so the first job on any slab, footing or base is to work out your volume. Get it wrong and you either run short mid-pour or pay for waste.',
    ],
    sections: [
      {
        h: 'The simple formula',
        p: ['Volume (m³) = length (m) × width (m) × depth (m). For example, a 4m × 3m slab at 100mm (0.1m) deep is 4 × 3 × 0.1 = 1.2 m³. Always add about 5–10% for sub-base unevenness and spillage.'],
        ul: ['Slab/base: length × width × thickness', 'Footing/trench: length × width × depth', 'Round up and add 5–10% contingency'],
      },
      {
        h: 'Bags vs. ready-mix',
        p: ['As a rough guide, a 25kg bag of pre-mixed concrete yields about 0.01 m³, so you need roughly 100 bags per m³ — fine for small jobs only. For anything above ~0.5 m³, ready-mix is usually cheaper and far less work. The Concrete Volume Calculator below gives you the m³ figure to order from.'],
      },
    ],
    faq: [
      { q: 'How do I calculate concrete in m³?', a: 'Multiply length × width × depth in metres. A 4×3m slab at 100mm deep = 1.2 m³. Add 5–10% for waste.' },
      { q: 'How many bags of concrete in a cubic metre?', a: 'Roughly 100 × 25kg bags per m³. Above about half a cubic metre, ready-mix is usually more economical.' },
    ],
    calc: { slug: 'concrete-volume', label: 'Concrete Volume Calculator', blurb: 'Work out the m³ of concrete for any slab, base or footing.' },
    relatedGuides: ['how-many-bricks-per-m2', 'sand-and-cement-mortar-mix'],
  },

  {
    slug: 'how-many-paving-slabs-do-i-need',
    h1: 'How Many Paving Slabs Do I Need?',
    metaTitle: 'How Many Paving Slabs Do I Need? (Calculator Guide)',
    metaDescription:
      'Work out how many paving slabs you need for any patio area and slab size, with a cutting allowance. Free paving slabs calculator.',
    kicker: '// How-To',
    updated: 'June 2026',
    intro: [
      'The number of slabs you need comes down to two things: the area you are covering and the size of each slab — plus a sensible allowance for cuts.',
    ],
    sections: [
      {
        h: 'How to work it out',
        p: ['Slabs per m² = 1 ÷ (slab length in m × slab width in m). A 600 × 600mm slab covers 0.36 m², so you need about 2.8 slabs per m². Multiply by your patio area and add 5–10% for cuts around edges and obstacles.'],
        ul: ['450×450mm: ~4.9 slabs per m²', '600×600mm: ~2.8 slabs per m²', '600×900mm: ~1.85 slabs per m²'],
      },
    ],
    faq: [
      { q: 'How many 600x600 slabs in a square metre?', a: 'About 2.8 slabs per m² (each covers 0.36 m²). Add 5–10% for cutting.' },
      { q: 'How do I work out slabs for a patio?', a: 'Divide your patio area by the area of one slab, then add a cutting allowance. The calculator below does this for any slab size.' },
    ],
    calc: { slug: 'paving-slabs', label: 'Paving Slabs Calculator', blurb: 'Exact slab count for any area and slab size.' },
    relatedGuides: ['patio-cost'],
  },

  {
    slug: 'sand-and-cement-mortar-mix',
    h1: 'Sand & Cement Mortar Mix Ratios and Quantities',
    metaTitle: 'Sand & Cement Mortar Mix Ratio (UK Guide + Calculator)',
    metaDescription:
      'The right sand and cement mortar mix ratios for bricklaying, pointing and rendering, plus how much you need. Free mortar mix calculator.',
    kicker: '// How-To',
    updated: 'June 2026',
    intro: [
      'Getting the mortar mix right matters for both strength and workability. Here are the common UK ratios and how to estimate quantities for your job.',
    ],
    sections: [
      {
        h: 'Common mortar mix ratios',
        p: ['Ratios are given as cement : sand (often with plasticiser or lime). The right one depends on exposure and the job.'],
        ul: [
          'General brick/blockwork: 1:4 to 1:5 (cement : sand)',
          'Exposed / below DPC: 1:3 to 1:4 (stronger)',
          'Pointing: 1:4 to 1:5',
          'Bricklaying with lime: 1:1:6 (cement : lime : sand)',
        ],
      },
      {
        h: 'How much will you need?',
        p: ['As a rough guide, a single-skin brick wall uses around 0.02–0.03 m³ of mortar per m². The Mortar Mix Calculator below converts that into bags of cement and tonnes/bags of sand for your chosen ratio.'],
      },
    ],
    faq: [
      { q: 'What is the best mortar mix for bricklaying?', a: 'A 1:4 to 1:5 cement-to-sand mix suits most general brickwork. Use a stronger 1:3 for exposed or below-ground work.' },
      { q: 'How much sand and cement do I need?', a: 'Around 0.02–0.03 m³ of mortar per m² of single-skin brickwork. The calculator converts this into bags of cement and sand.' },
    ],
    calc: { slug: 'mortar-mix', label: 'Mortar Mix Calculator', blurb: 'Cement and sand quantities for any mix ratio and area.' },
    relatedGuides: ['how-many-bricks-per-m2'],
  },

  {
    slug: 'tradesman-day-rate',
    h1: 'What Day Rate Should a Tradesman Charge?',
    metaTitle: 'Tradesman Day Rate UK — What Should You Charge?',
    metaDescription:
      'How to set a profitable day rate as a UK tradesman — covering overheads, holidays and profit. Free day rate calculator.',
    kicker: '// Business',
    updated: 'June 2026',
    intro: [
      'Charging by the day is simple — but pick the wrong number and you either price yourself out of work or quietly lose money. Here is how to set a day rate that actually covers your costs and leaves a profit.',
    ],
    sections: [
      {
        h: 'Why "what everyone else charges" is the wrong starting point',
        p: ['Your day rate has to cover far more than the hours on site. Build it up from your real numbers: the income you want, plus overheads (van, insurance, tools, fuel, accountant), divided by the days you can realistically bill in a year after holidays, illness and quoting time.'],
        ul: [
          'Target annual take-home you want',
          'Annual overheads (van, fuel, insurance, tools, phone, software)',
          'Billable days per year (allow for holidays, sick days, admin)',
          'Add a profit margin on top — your business should earn, not just you',
        ],
      },
      {
        h: 'Work it out in seconds',
        p: ['The Day Rate Calculator below takes your target earnings, overheads and billable days and tells you the day rate you need to charge. Pair it with the estimator to turn day rates into full branded quotes.'],
      },
    ],
    faq: [
      { q: 'How do I calculate my day rate?', a: 'Add your target earnings and annual overheads, then divide by your realistic billable days per year, and add a profit margin. The calculator below does this for you.' },
      { q: 'What is the average tradesman day rate in the UK?', a: 'It varies widely by trade and region, but the right number for you is the one that covers your overheads and leaves a profit — not just the local average.' },
    ],
    calc: { slug: 'day-rate-target', label: 'Day Rate Calculator', blurb: 'Find the day rate you need to cover costs and profit.' },
    relatedGuides: [],
  },

  {
    slug: 'how-to-size-a-cable-bs7671',
    h1: 'How to Size a Cable (BS 7671)',
    metaTitle: 'How to Size a Cable — BS 7671 Step-by-Step (UK)',
    metaDescription:
      'Size a cable correctly to BS 7671: design current, protective device, correction factors (Ca·Cg·Ci·Cf), current-carrying capacity and voltage drop. Free cable size calculator.',
    kicker: '// Electrical',
    updated: 'June 2026',
    intro: [
      'Cable sizing is the calculation electricians do most often — and getting it wrong means nuisance tripping, overheating or a failed EICR. BS 7671 sets out a clear method, and once you know the steps it takes seconds.',
      'This guide walks through the full process, then you can run the numbers instantly with the free Cable Size Calculator.',
    ],
    sections: [
      {
        h: 'The BS 7671 cable sizing steps',
        p: ['Sizing a cable is really five checks. Work through them in order:'],
        ul: [
          'Design current (Ib) — the load the circuit will actually carry',
          'Protective device (In) — must be ≥ Ib and protect the cable (In ≥ Ib)',
          'Correction factors — ambient temperature (Ca), grouping (Cg), insulation (Ci) and device type (Cf)',
          'Tabulated capacity (It) — It = In ÷ (Ca × Cg × Ci × Cf); pick a cable whose rating Iz ≥ It',
          'Voltage drop — check the volt drop over the run is within 3% (lighting) or 5% (power)',
        ],
      },
      {
        h: 'Why correction factors matter',
        p: ['A cable\'s tabulated rating assumes ideal conditions (30°C, on its own, not in insulation). Real installations are hotter, bunched together, or buried in loft insulation — each of which reduces how much current the cable can safely carry. Dividing In by the product of the correction factors gives the rating the cable must have in those conditions.'],
      },
      {
        h: 'Don\'t forget voltage drop',
        p: ['A cable can be big enough on current capacity but still fail on voltage drop over a long run. The calculator below checks both and tells you which factor decided the final size — exactly as you would on a design.'],
      },
    ],
    faq: [
      { q: 'What size cable for a 32A circuit?', a: 'It depends on the installation method and correction factors, but a 32A ring or radial on twin & earth is commonly 2.5mm² or 4mm²/6mm² depending on length, grouping and insulation. Use the calculator to confirm against current capacity and voltage drop.' },
      { q: 'What are the correction factors in BS 7671?', a: 'Ca (ambient temperature), Cg (grouping of circuits), Ci (thermal insulation) and Cf (protective device type — e.g. 0.725 for a BS3036 rewireable fuse). You divide the device rating by their product to get the required cable rating.' },
      { q: 'What is the maximum voltage drop allowed?', a: 'BS 7671 recommends 3% for lighting and 5% for other uses, measured from the origin to the furthest point of the circuit.' },
    ],
    calc: { slug: 'cable-size', label: 'Cable Size Calculator', blurb: 'Full BS 7671 workflow — capacity, correction factors and voltage drop in one.' },
    relatedGuides: [],
  },

  {
    slug: 'what-size-radiator-do-i-need',
    h1: 'What Size Radiator Do I Need? (BTU Guide)',
    metaTitle: 'What Size Radiator Do I Need? BTU Calculator Guide (UK)',
    metaDescription:
      'How to work out the radiator size (BTU) a room needs — the simple method, what affects it, and a free radiator BTU calculator for any UK room.',
    kicker: '// Heating',
    updated: 'June 2026',
    intro: [
      'A radiator that is too small never warms the room; one that is too big wastes money. "BTU" (British Thermal Units per hour) is just the heat output a radiator gives — and the right figure depends mostly on the size and type of room.',
    ],
    sections: [
      {
        h: 'The quick method',
        p: ['Work out the room volume (length × width × height in metres), then multiply by a heat factor for the room type. Living rooms and bathrooms need more heat per cubic metre than bedrooms. Convert watts to BTU by multiplying by 3.412.'],
        ul: [
          'Lounge / dining: ~70 W per m³',
          'Bedroom / kitchen: ~50 W per m³',
          'Bathroom: ~80 W per m³',
          'Add for north-facing rooms, large windows or poor insulation',
        ],
      },
      {
        h: 'What pushes the BTU up',
        p: ['North-facing rooms, French doors and patio windows, rooms above unheated spaces, and older solid-wall properties all lose more heat — so add a margin. The calculator below lets you pick the room type and gives both watts and BTU.'],
      },
    ],
    faq: [
      { q: 'How many BTUs do I need to heat a room?', a: 'As a rough guide, multiply the room volume (m³) by 50–80 W depending on room type, then ×3.412 for BTU. A typical 4×4×2.4m lounge needs roughly 9,000–11,000 BTU.' },
      { q: 'Is it better to oversize a radiator?', a: 'A small margin is sensible, but a much-oversized radiator wastes energy and space. Size to the room and let the thermostatic valve control the output.' },
    ],
    calc: { slug: 'radiator-btu', label: 'Radiator BTU Calculator', blurb: 'Work out the heat output (W and BTU) any room needs.' },
    relatedGuides: ['what-size-combi-boiler-do-i-need'],
  },

  {
    slug: 'what-size-combi-boiler-do-i-need',
    h1: 'What Size Combi Boiler Do I Need?',
    metaTitle: 'What Size Combi Boiler Do I Need? (UK Sizing Guide)',
    metaDescription:
      'How to choose the right combi boiler kW output for your home — by radiators, bathrooms and hot water demand. Free boiler sizing calculator.',
    kicker: '// Heating',
    updated: 'June 2026',
    intro: [
      'With a combi boiler the output (kW) has to do two jobs: heat the radiators and deliver instant hot water. Hot water is usually the deciding factor, so combi sizing is driven mainly by how many bathrooms and outlets the home has.',
    ],
    sections: [
      {
        h: 'Rough combi sizing by home',
        p: ['As a starting point:'],
        ul: [
          'Up to ~10 radiators, 1 bathroom: 24–27 kW',
          '10–15 radiators, 2 bathrooms: 28–34 kW',
          '15–20 radiators, 2–3 bathrooms: 35–42 kW',
        ],
      },
      {
        h: 'Heat demand vs. hot water',
        p: ['The radiators might only need 8–12 kW, but a combi has to deliver enough flow of hot water on demand — which is why combis are rated higher. If hot-water demand is high (multiple showers at once), a system boiler with a cylinder may suit better. Use the calculator to get a recommended output range, and pair it with the heat-loss calculator for the heating side.'],
      },
    ],
    faq: [
      { q: 'What kW combi boiler for a 3-bed house?', a: 'A typical 3-bed home with one bathroom and ~10–12 radiators is usually well matched by a 28–30 kW combi. Add output if you have a second bathroom or high hot-water demand.' },
      { q: 'Is a bigger combi boiler better?', a: 'Only up to a point — an oversized combi cycles inefficiently. Size it to your hot-water demand and radiator load rather than fitting the biggest available.' },
    ],
    calc: { slug: 'boiler-size', label: 'Combi Boiler Sizing Calculator', blurb: 'Recommended boiler output from radiators and bathrooms.' },
    relatedGuides: ['what-size-radiator-do-i-need'],
  },
  {
    slug: 'van-fuel-costs',
    h1: 'How to Cut Fuel Costs for Your Work Van (2026)',
    metaTitle: 'How to Cut Van Fuel Costs — UK Tradesman Guide (2026)',
    metaDescription:
      'Fuel is one of the biggest running costs for a UK work van. Practical ways tradesmen can cut diesel and petrol costs in 2026 — plus a free live fuel price checker.',
    kicker: '// Fuel & Running Costs',
    updated: 'June 2026',
    intro: [
      'For most UK tradesmen the work van is the second-biggest overhead after wages — and fuel is the single largest part of running it. A busy sole trader doing 20,000–30,000 miles a year can easily spend £3,000–£5,000 on diesel alone, so trimming even a few pence a litre and a few unnecessary miles adds up to real money over a year.',
      'This guide covers the practical ways to cut van fuel costs in 2026, from finding the cheapest forecourt to smarter routing and driving — plus a free live fuel-price tracker so you never overpay at the pump.',
    ],
    sections: [
      {
        h: 'Always fill up at the cheapest local forecourt',
        p: ['Prices between forecourts just a mile apart can differ by 10p a litre or more. Supermarket sites (Asda, Tesco, Morrisons, Sainsbury\u2019s) are almost always cheaper than branded or motorway forecourts. Checking live prices before you fill is the single easiest saving.'],
        ul: [
          'Use the free TradesMate fuel tracker to find the cheapest petrol or diesel near you',
          'Fill up before joining a motorway — service-station fuel is the dearest of all',
          'Top up in full rather than little-and-often to cut wasted trips',
        ],
      },
      {
        h: 'Cut the miles, not just the price',
        p: ['The cheapest litre is the one you never burn. Plan jobs by area so you are not crossing town twice, batch material runs, and use the quietest times to avoid idling in traffic. A well-planned week can knock 10–15% off your mileage.'],
      },
      {
        h: 'Keep the van efficient',
        p: ['Under-inflated tyres, a roof rack left on, and a van full of kit you don\u2019t need all raise consumption. Regular servicing, correct tyre pressures and stripping out dead weight can improve MPG by several percent for almost no cost.'],
        ul: ['Check tyre pressures weekly', 'Remove roof racks/ladders when not needed', 'Service on schedule — a clogged filter costs MPG'],
      },
      {
        h: 'Don\u2019t forget the tax side',
        p: ['If you\u2019re self-employed you can claim fuel and van running costs against tax — either as actual costs or via the simplified mileage rate. Keeping fuel receipts and mileage records means you claim every penny you\u2019re entitled to. See our guide on claiming fuel and mileage below.'],
      },
    ],
    faq: [
      { q: 'How much does fuel cost a UK tradesman per year?', a: 'A van doing 20,000–30,000 miles a year typically costs £3,000–£5,000 in fuel, depending on the van, mileage and pump prices. Finding the cheapest local fuel and cutting unnecessary miles are the biggest levers.' },
      { q: 'What is the cheapest place to buy fuel?', a: 'Supermarket forecourts are usually the cheapest, and motorway services the most expensive. Use a live fuel-price checker to compare forecourts near you before filling up.' },
      { q: 'Can I claim van fuel against tax?', a: 'Yes — if you\u2019re self-employed you can claim van fuel and running costs, either as actual expenses or using HMRC\u2019s simplified mileage rate. Keep receipts and a mileage log.' },
    ],
    cta: { to: '/fuel', label: 'Check live fuel prices near you', blurb: 'Find the cheapest petrol and diesel near you right now — free, no sign-up.' },
    relatedGuides: ['petrol-vs-diesel-van', 'claiming-fuel-mileage-tax'],
  },

  {
    slug: 'petrol-vs-diesel-van',
    h1: 'Petrol vs Diesel Van: Which Is Cheaper to Run?',
    metaTitle: 'Petrol vs Diesel Van — Which Is Cheaper to Run? (2026 UK)',
    metaDescription:
      'Petrol vs diesel work van running costs compared for 2026 — fuel economy, price per litre, servicing and resale. Plus a free live fuel price checker.',
    kicker: '// Fuel & Running Costs',
    updated: 'June 2026',
    intro: [
      'Choosing between a petrol and a diesel van is one of the biggest running-cost decisions a UK tradesman makes. Diesel has long been the default for vans, but with cleaner petrol engines and changing fuel prices, the answer in 2026 depends on how you actually use the van.',
    ],
    sections: [
      {
        h: 'Fuel economy and mileage',
        p: ['Diesel vans still typically return better MPG than petrol equivalents, especially when loaded and on longer runs — often 20–30% more miles per gallon. That advantage grows the more miles you do, which is why high-mileage tradesmen usually still favour diesel.'],
        ul: [
          'High mileage (15,000+ miles/yr, motorways): diesel usually wins',
          'Low mileage, mostly urban/short trips: petrol can work out cheaper overall',
          'Diesel particulate filters (DPFs) dislike lots of short stop-start journeys',
        ],
      },
      {
        h: 'Price at the pump',
        p: ['Diesel and petrol prices don\u2019t always move together, and the gap between them changes month to month. Because diesel is sometimes dearer per litre, the MPG advantage doesn\u2019t always translate into a lower cost per mile — so it pays to check current prices rather than assume. Use the live tracker to compare both today.'],
      },
      {
        h: 'Servicing, tax and resale',
        p: ['Diesels can cost a little more to service (DPF, injectors) but often hold their value better on the used market for vans. Petrol vans are usually cheaper to buy new. Factor in the whole picture — purchase price, fuel, servicing and resale — not just MPG.'],
      },
    ],
    faq: [
      { q: 'Is a diesel or petrol van cheaper to run?', a: 'For high-mileage tradesmen, diesel is usually cheaper per mile thanks to better MPG. For low-mileage, mostly-urban use, a petrol van can work out cheaper and avoids DPF issues from short trips.' },
      { q: 'Do diesel vans get better MPG?', a: 'Generally yes — often 20–30% more miles per gallon than an equivalent petrol van, especially when loaded or on longer journeys.' },
      { q: 'Is diesel more expensive than petrol?', a: 'Sometimes. The price gap changes through the year, so check live pump prices rather than assuming — a live fuel tracker lets you compare petrol and diesel near you.' },
    ],
    cta: { to: '/fuel', label: 'Compare live petrol & diesel prices', blurb: 'See today\u2019s cheapest petrol and diesel near you before you fill up.' },
    relatedGuides: ['van-fuel-costs', 'claiming-fuel-mileage-tax'],
  },

  {
    slug: 'claiming-fuel-mileage-tax',
    h1: 'Claiming Fuel & Mileage on Your Tax Return (UK Sole Trader)',
    metaTitle: 'Claiming Van Fuel & Mileage on Tax — UK Sole Trader Guide',
    metaDescription:
      'How UK self-employed tradesmen claim van fuel and mileage against tax in 2026 — actual costs vs the simplified mileage rate, what records to keep, and common mistakes.',
    kicker: '// Fuel & Tax',
    updated: 'June 2026',
    intro: [
      'If you\u2019re a self-employed tradesman, the fuel and running costs of your work van are a legitimate business expense — but many sole traders either under-claim or keep such poor records that they can\u2019t back up the claim. Here\u2019s how it works in 2026 and how to claim every penny you\u2019re entitled to.',
    ],
    sections: [
      {
        h: 'Two ways to claim: actual costs vs simplified mileage',
        p: ['HMRC lets sole traders claim van costs one of two ways. You pick whichever suits, but you generally have to stick with your choice for that vehicle.'],
        ul: [
          'Actual costs: claim the business proportion of fuel, servicing, insurance, repairs and capital allowances — keep all receipts',
          'Simplified mileage: claim a flat rate per business mile (45p first 10,000 miles, 25p after, for cars/vans) instead of actual running costs',
          'You can\u2019t mix both methods for the same vehicle in the same year',
        ],
      },
      {
        h: 'What records to keep',
        p: ['Whichever method you use, records are everything. For actual costs, keep every fuel and repair receipt. For mileage, keep a simple log of business journeys (date, from/to, miles, reason). Snapping receipts as you go beats a shoebox in January.'],
      },
      {
        h: 'Common mistakes',
        p: ['The biggest errors are claiming 100% of a van that\u2019s also used privately, losing receipts, and forgetting VAT if you\u2019re registered. If you\u2019re VAT-registered you can usually reclaim VAT on van fuel (subject to the fuel scale charge for private use). Keep the business/private split honest and documented.'],
      },
    ],
    faq: [
      { q: 'Can I claim van fuel on my tax return?', a: 'Yes. Self-employed tradesmen can claim the business proportion of van fuel and running costs as actual expenses, or use HMRC\u2019s simplified flat mileage rate instead. You can\u2019t use both for the same van.' },
      { q: 'What is the HMRC mileage rate for 2026?', a: 'For cars and vans the simplified rate is 45p per mile for the first 10,000 business miles in the year and 25p per mile after that. Check GOV.UK for the current figures before filing.' },
      { q: 'Do I need to keep fuel receipts?', a: 'Yes if you claim actual costs. If you use the simplified mileage method you mainly need a mileage log, but keeping receipts is still good practice — and essential if you\u2019re VAT-registered.' },
    ],
    cta: { to: '/receipts', label: 'Scan & track your receipts', blurb: 'TradesMate can scan fuel and material receipts and pull out the VAT automatically — ready for your return.' },
    relatedGuides: ['van-fuel-costs', 'petrol-vs-diesel-van', 'tradesman-day-rate'],
  },
];

export const guideBySlug = (slug) => GUIDES.find((g) => g.slug === slug);
export const guideForCalc = (calcSlug) => GUIDES.find((g) => g.calc && g.calc.slug === calcSlug);
